import {
  OFFLINE_REWARD_RECEIPT_VERSION,
  type OfflineRewardReceipt,
} from "@/features/hoverIdle/offline/offlineRewardReceipt";
import { deserializeHuge, serializeHuge } from "@/shared/numbers";

export const APPLIED_REWARD_ID_HISTORY_LIMIT = 64;

export interface OfflineRewardSaveMetadata {
  readonly revision: number;
  readonly baselineAt: number;
  readonly lastReceipt: OfflineRewardReceipt | null;
  readonly appliedRewardIds: readonly string[];
}

export function createDefaultOfflineRewardSaveMetadata(
  baselineAt = 0,
): OfflineRewardSaveMetadata {
  return { revision: 0, baselineAt, lastReceipt: null, appliedRewardIds: [] };
}

export function cloneOfflineRewardSaveMetadata(
  metadata: OfflineRewardSaveMetadata,
): OfflineRewardSaveMetadata {
  return {
    revision: metadata.revision,
    baselineAt: metadata.baselineAt,
    lastReceipt:
      metadata.lastReceipt === null
        ? null
        : {
            ...metadata.lastReceipt,
            baseline: { ...metadata.lastReceipt.baseline },
          },
    appliedRewardIds: [...metadata.appliedRewardIds],
  };
}

export function advanceOfflineRewardRevision(
  metadata: OfflineRewardSaveMetadata,
  baselineAt = metadata.baselineAt,
): OfflineRewardSaveMetadata {
  if (!Number.isSafeInteger(metadata.revision) || metadata.revision < 0) {
    throw new Error(
      "offline reward revision must be a non-negative safe integer",
    );
  }
  return {
    ...cloneOfflineRewardSaveMetadata(metadata),
    revision: metadata.revision + 1,
    baselineAt,
  };
}

export function appendAppliedRewardId(
  ids: readonly string[],
  rewardId: string,
): readonly string[] {
  return [...ids, rewardId].slice(-APPLIED_REWARD_ID_HISTORY_LIMIT);
}

export function validateOfflineRewardSaveMetadata(
  value: unknown,
  path = "offlineReward",
): string | null {
  if (!isRecord(value)) return `${path} must be an object`;
  if (!Number.isSafeInteger(value.revision) || (value.revision as number) < 0) {
    return `${path}.revision must be a non-negative safe integer`;
  }
  if (!isFiniteNonNegativeNumber(value.baselineAt)) {
    return `${path}.baselineAt must be finite and non-negative`;
  }
  if (!Array.isArray(value.appliedRewardIds)) {
    return `${path}.appliedRewardIds must be an array`;
  }
  if (value.appliedRewardIds.length > APPLIED_REWARD_ID_HISTORY_LIMIT) {
    return `${path}.appliedRewardIds exceeds its bounded history limit`;
  }
  const ids = value.appliedRewardIds;
  if (ids.some((id) => typeof id !== "string" || id.length === 0)) {
    return `${path}.appliedRewardIds must contain non-empty strings`;
  }
  if (new Set(ids).size !== ids.length) {
    return `${path}.appliedRewardIds must not contain duplicates`;
  }
  if (value.lastReceipt === null) return null;
  return validateOfflineRewardReceipt(value.lastReceipt, `${path}.lastReceipt`);
}

function validateOfflineRewardReceipt(
  value: unknown,
  path: string,
): string | null {
  if (!isRecord(value)) return `${path} must be an object or null`;
  if (value.version !== OFFLINE_REWARD_RECEIPT_VERSION) {
    return `${path}.version must be ${OFFLINE_REWARD_RECEIPT_VERSION}`;
  }
  for (const key of ["rewardId", "resultDigest"] as const) {
    if (typeof value[key] !== "string" || value[key].length === 0) {
      return `${path}.${key} must be a non-empty string`;
    }
  }
  if (!isFiniteNonNegativeNumber(value.gameElapsedMs)) {
    return `${path}.gameElapsedMs must be finite and non-negative`;
  }
  if (typeof value.wasCapped !== "boolean") {
    return `${path}.wasCapped must be a boolean`;
  }
  if (!isCanonicalHugeString(value.timGained)) {
    return `${path}.timGained must use canonical HugeNumber serialization`;
  }
  if (
    !Number.isSafeInteger(value.commitRevision) ||
    (value.commitRevision as number) < 0
  ) {
    return `${path}.commitRevision must be a non-negative safe integer`;
  }
  if (!isRecord(value.baseline)) {
    return `${path}.baseline must be an object`;
  }
  if (
    !isFiniteNonNegativeNumber(value.baseline.previousAt) ||
    !isFiniteNonNegativeNumber(value.baseline.committedAt) ||
    value.baseline.committedAt < value.baseline.previousAt
  ) {
    return `${path}.baseline must contain an ordered non-negative interval`;
  }
  return null;
}

function isCanonicalHugeString(value: unknown): boolean {
  if (typeof value !== "string") return false;
  try {
    return serializeHuge(deserializeHuge(value)) === value;
  } catch {
    return false;
  }
}

function isFiniteNonNegativeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
